import { useCallback, useMemo, useReducer, useRef, useState } from 'react'

import { BLOCKS_PER_SLIDE_CAP, LAYOUT_REGIONS, SLIDE_CAP, UNDO_LIMIT } from '../constants'
import type {
	Block,
	DeckDefinition,
	EntityRef,
	Presentation,
	Slide,
	SlideLayout,
	Story,
	Transition,
} from '../types/presentation.types'
import * as ops from '../utils/deckOps'

/**
 * The deck being edited, with undo (MYD-493). Modelled on
 * `useExplorerDefinition`: live state, the presentation it was loaded from, and
 * a `dirty` flag measured against a JSON baseline.
 *
 * ## History
 *
 * A reducer holds `{ past, present, future }`, so an edit and its undo entry
 * land atomically. Every operation is a pure function from `utils/deckOps`;
 * one that does not apply returns the same deck, and the reducer drops it, so
 * a refused edit (a seventh block) never becomes an undo step.
 *
 * Text edits COALESCE. Typing a slide title is one undo step, not one per
 * keystroke: consecutive edits to the same field of the same slide replace
 * the present instead of pushing onto the stack. Any other edit ends the run.
 * Without that, a sentence of typing would exhaust the 50-step stack.
 *
 * ## Dirty
 *
 * Compared with a key-order-insensitive stringify (`stableStringify`),
 * because the server rebuilds objects in its own key order. `dirty` is false
 * after `load`, `startBlank` and `markSaved`, and after undoing back to the
 * saved state.
 */

type Recipe = (deck: DeckDefinition) => DeckDefinition

interface History {
	past: DeckDefinition[]
	present: DeckDefinition
	future: DeckDefinition[]
	/** The coalescing key of the edit that produced `present`, if any. */
	lastKey: string | null
}

type Action =
	| { type: 'apply'; recipe: Recipe; coalesceKey?: string }
	| { type: 'undo' }
	| { type: 'redo' }
	| { type: 'reset'; deck: DeckDefinition }
	/** Swap the present without touching history. Used after a save only. */
	| { type: 'replacePresent'; deck: DeckDefinition }

const fresh = (deck: DeckDefinition): History => ({ past: [], present: deck, future: [], lastKey: null })

export const historyReducer = (state: History, action: Action): History => {
	switch (action.type) {
		case 'apply': {
			const next = action.recipe(state.present)
			if (next === state.present) return state

			if (action.coalesceKey && action.coalesceKey === state.lastKey) {
				return { ...state, present: next, future: [] }
			}
			return {
				past: [...state.past, state.present].slice(-UNDO_LIMIT),
				present: next,
				future: [],
				lastKey: action.coalesceKey ?? null,
			}
		}
		case 'undo': {
			if (state.past.length === 0) return state
			return {
				past: state.past.slice(0, -1),
				present: state.past[state.past.length - 1],
				future: [state.present, ...state.future],
				lastKey: null,
			}
		}
		case 'redo': {
			if (state.future.length === 0) return state
			return {
				past: [...state.past, state.present].slice(-UNDO_LIMIT),
				present: state.future[0],
				future: state.future.slice(1),
				lastKey: null,
			}
		}
		case 'reset':
			return fresh(action.deck)
		case 'replacePresent':
			return { ...state, present: action.deck, lastKey: null }
		default:
			return state
	}
}

export interface UseDeckEditor {
	deck: DeckDefinition
	loaded: Presentation | null
	dirty: boolean
	canUndo: boolean
	canRedo: boolean

	load: (presentation: Presentation) => void
	startBlank: () => void
	/**
	 * Call with the server's response and the definition that was SENT. If
	 * nothing changed while the save was in flight, the editor adopts the
	 * server's normalised definition (config defaults filled in); otherwise it
	 * keeps the newer local edits and stays dirty.
	 */
	markSaved: (saved: Presentation, sent: DeckDefinition) => void
	toDefinition: () => DeckDefinition

	undo: () => void
	redo: () => void

	setDeckTransition: (transition: Transition) => void

	/** Each add returns the new id, or null when the edit was refused. */
	addStory: (options?: Partial<Pick<Story, 'title' | 'anchor'>> & { index?: number }) => string
	/**
	 * Put in whole stories, such as ones made from a template (MYD-505), as
	 * one undoable edit with fresh ids. Returns each new story's id and slide
	 * ids, or null when they would take the deck past its slide cap.
	 */
	insertStories: (stories: Story[], options?: { index?: number }) => Array<{ storyId: string; slideIds: string[] }> | null
	updateStory: (storyId: string, patch: Partial<Pick<Story, 'title' | 'anchor' | 'divider'>>) => void
	moveStory: (storyId: string, toIndex: number) => void
	deleteStory: (storyId: string) => void

	addSlide: (storyId: string, options?: { layout?: SlideLayout; index?: number }) => string | null
	updateSlide: (slideId: string, patch: Partial<Pick<Slide, 'title' | 'subtitle' | 'speakerNotes' | 'transition'>>, options?: EditOptions) => void
	moveSlide: (slideId: string, toStoryId: string, toIndex: number) => void
	/** Copies the slide right after itself, with fresh ids. Returns the copy's id, or null at the cap. */
	duplicateSlide: (slideId: string) => string | null
	deleteSlide: (slideId: string) => void
	setLayout: (slideId: string, layout: SlideLayout) => void

	addBlock: (
		slideId: string,
		block: Pick<Block, 'region' | 'element'> & Partial<Pick<Block, 'config' | 'source'>>,
		options?: { index?: number },
	) => string | null
	moveBlock: (blockId: string, to: { slideId?: string; region?: string; index?: number }) => void
	deleteBlock: (blockId: string) => void
	setBlockConfig: (blockId: string, config: Record<string, unknown>, options?: EditOptions) => void
	setBlockSource: (blockId: string, source: EntityRef | null) => void
}

/**
 * `ownStep`: the edit is one undo step of its own, never merged into a run of
 * typing, so undo takes back exactly it. For text that arrives whole, like an
 * AI draft (MYD-640).
 */
export interface EditOptions {
	ownStep?: boolean
}

/** The single field a text patch touches, for coalescing; null if several. */
const textKey = (scope: string, id: string, patch: object) => {
	const keys = Object.keys(patch)
	return keys.length === 1 ? `${scope}:${id}:${keys[0]}` : undefined
}

export function useDeckEditor(): UseDeckEditor {
	const [history, dispatch] = useReducer(historyReducer, undefined, () => fresh(ops.emptyDeck()))
	const [loaded, setLoaded] = useState<Presentation | null>(null)
	const [baseline, setBaseline] = useState(() => ops.stableStringify(ops.emptyDeck()))

	// The latest deck, readable from callbacks without making every callback
	// depend on it (which would give them a new identity on every keystroke).
	// Used only to predict whether an add will be refused, so it can return
	// null; the reducer re-checks against the real present regardless.
	const deckRef = useRef(history.present)
	deckRef.current = history.present

	const deck = history.present
	const serialized = useMemo(() => ops.stableStringify(deck), [deck])
	const dirty = serialized !== baseline

	const apply = useCallback((recipe: Recipe, coalesceKey?: string) => dispatch({ type: 'apply', recipe, coalesceKey }), [])

	const load = useCallback((presentation: Presentation) => {
		dispatch({ type: 'reset', deck: presentation.definition })
		setLoaded(presentation)
		setBaseline(ops.stableStringify(presentation.definition))
	}, [])

	const startBlank = useCallback(() => {
		const blank = ops.emptyDeck()
		dispatch({ type: 'reset', deck: blank })
		setLoaded(null)
		setBaseline(ops.stableStringify(blank))
	}, [])

	const markSaved = useCallback((saved: Presentation, sent: DeckDefinition) => {
		setLoaded(saved)
		setBaseline(ops.stableStringify(saved.definition))
		// Baseline from the PERSISTED definition either way. Adopting it only
		// when nothing changed mid-save means an edit made during the request is
		// neither lost nor wrongly marked clean.
		if (ops.stableStringify(deckRef.current) === ops.stableStringify(sent)) {
			dispatch({ type: 'replacePresent', deck: saved.definition })
		}
	}, [])

	const toDefinition = useCallback(() => deckRef.current, [])

	const undo = useCallback(() => dispatch({ type: 'undo' }), [])
	const redo = useCallback(() => dispatch({ type: 'redo' }), [])

	const setDeckTransition = useCallback((transition: Transition) => apply(d => ops.setDeckTransition(d, transition)), [apply])

	const addStory = useCallback<UseDeckEditor['addStory']>(
		(options = {}) => {
			const id = ops.newId()
			apply(d => ops.addStory(d, id, options))
			return id
		},
		[apply],
	)
	const insertStories = useCallback<UseDeckEditor['insertStories']>(
		(stories, options = {}) => {
			const adding = stories.reduce((total, story) => total + story.slides.length, 0)
			if (stories.length === 0 || ops.countSlides(deckRef.current) + adding > SLIDE_CAP) return null
			// Ids drawn now, not inside the recipe, so the caller learns the
			// story ids and a replayed recipe (redo) reuses the same ones.
			const drawn: string[] = []
			for (const story of stories) {
				drawn.push(ops.newId())
				for (const slide of story.slides) {
					drawn.push(ops.newId())
					for (let block = 0; block < slide.blocks.length; block += 1) drawn.push(ops.newId())
				}
			}
			// The same walk as insertStories', to name what was made.
			const made: Array<{ storyId: string; slideIds: string[] }> = []
			let cursor = 0
			for (const story of stories) {
				const entry = { storyId: drawn[cursor++], slideIds: [] as string[] }
				for (const slide of story.slides) {
					entry.slideIds.push(drawn[cursor++])
					cursor += slide.blocks.length
				}
				made.push(entry)
			}
			apply(d => {
				let next = 0
				return ops.insertStories(d, stories, () => drawn[next++], options.index)
			})
			return made
		},
		[apply],
	)
	const updateStory = useCallback<UseDeckEditor['updateStory']>(
		// The divider switch is a click, not typing, so it is its own undo step.
		(storyId, patch) => apply(d => ops.updateStory(d, storyId, patch), 'divider' in patch ? undefined : textKey('story', storyId, patch)),
		[apply],
	)
	const moveStory = useCallback((storyId: string, toIndex: number) => apply(d => ops.moveStory(d, storyId, toIndex)), [apply])
	const deleteStory = useCallback((storyId: string) => apply(d => ops.deleteStory(d, storyId)), [apply])

	const addSlide = useCallback<UseDeckEditor['addSlide']>(
		(storyId, options = {}) => {
			const current = deckRef.current
			if (!current.stories.some(story => story.id === storyId) || ops.countSlides(current) >= SLIDE_CAP) return null
			const id = ops.newId()
			apply(d => ops.addSlide(d, storyId, id, options))
			return id
		},
		[apply],
	)
	const updateSlide = useCallback<UseDeckEditor['updateSlide']>(
		(slideId, patch, options = {}) => {
			// Transition is a click, not typing, so it is its own undo step.
			const key = 'transition' in patch || options.ownStep ? undefined : textKey('slide', slideId, patch)
			apply(d => ops.updateSlide(d, slideId, patch), key)
		},
		[apply],
	)
	const moveSlide = useCallback(
		(slideId: string, toStoryId: string, toIndex: number) => apply(d => ops.moveSlide(d, slideId, toStoryId, toIndex)),
		[apply],
	)
	const duplicateSlide = useCallback<UseDeckEditor['duplicateSlide']>(
		slideId => {
			const current = deckRef.current
			const found = ops.findSlide(current, slideId)
			if (!found || ops.countSlides(current) >= SLIDE_CAP) return null
			const ids = { slide: ops.newId(), blocks: found.slide.blocks.map(() => ops.newId()) }
			apply(d => ops.duplicateSlide(d, slideId, ids))
			return ids.slide
		},
		[apply],
	)
	const deleteSlide = useCallback((slideId: string) => apply(d => ops.deleteSlide(d, slideId)), [apply])
	const setLayout = useCallback((slideId: string, layout: SlideLayout) => apply(d => ops.setLayout(d, slideId, layout)), [apply])

	const addBlock = useCallback<UseDeckEditor['addBlock']>(
		(slideId, block, options = {}) => {
			const found = ops.findSlide(deckRef.current, slideId)
			if (!found) return null
			if (found.slide.blocks.length >= BLOCKS_PER_SLIDE_CAP) return null
			if (!LAYOUT_REGIONS[found.slide.layout].includes(block.region)) return null

			const id = ops.newId()
			apply(d => ops.addBlock(d, slideId, ops.makeBlock(id, block), options))
			return id
		},
		[apply],
	)
	const moveBlock = useCallback<UseDeckEditor['moveBlock']>((blockId, to) => apply(d => ops.moveBlock(d, blockId, to)), [apply])
	const deleteBlock = useCallback((blockId: string) => apply(d => ops.deleteBlock(d, blockId)), [apply])
	// The inspector edits config field by field as the user types, so config
	// edits to one block coalesce like text.
	const setBlockConfig = useCallback(
		(blockId: string, config: Record<string, unknown>, options: EditOptions = {}) =>
			apply(d => ops.setBlockConfig(d, blockId, config), options.ownStep ? undefined : `config:${blockId}`),
		[apply],
	)
	const setBlockSource = useCallback(
		(blockId: string, source: EntityRef | null) => apply(d => ops.setBlockSource(d, blockId, source)),
		[apply],
	)

	// Memoised so consumers can list the editor in dependency arrays honestly;
	// see the same note in useExplorerDefinition.
	return useMemo(
		() => ({
			deck,
			loaded,
			dirty,
			canUndo: history.past.length > 0,
			canRedo: history.future.length > 0,
			load,
			startBlank,
			markSaved,
			toDefinition,
			undo,
			redo,
			setDeckTransition,
			addStory,
			insertStories,
			updateStory,
			moveStory,
			deleteStory,
			addSlide,
			updateSlide,
			moveSlide,
			duplicateSlide,
			deleteSlide,
			setLayout,
			addBlock,
			moveBlock,
			deleteBlock,
			setBlockConfig,
			setBlockSource,
		}),
		[
			deck,
			loaded,
			dirty,
			history.past.length,
			history.future.length,
			load,
			startBlank,
			markSaved,
			toDefinition,
			undo,
			redo,
			setDeckTransition,
			addStory,
			insertStories,
			updateStory,
			moveStory,
			deleteStory,
			addSlide,
			updateSlide,
			moveSlide,
			duplicateSlide,
			deleteSlide,
			setLayout,
			addBlock,
			moveBlock,
			deleteBlock,
			setBlockConfig,
			setBlockSource,
		],
	)
}

export default useDeckEditor
